/**
 * Einfache Tatsachen über einen Rechnungsdatensatz. Gemeinsame Grundlage für
 * Zustandsableitung, Mahnwesen und Monatsstatus – damit alle dieselben Begriffe nutzen.
 */
import { DUNNING_COLLECTION, DUNNING_NONE, type InvoiceRecord, type IsoDate } from "./types.js";

/** RE-04: bezahlt, sobald paidAt gesetzt ODER Zahlstatus 'paid'. */
export function isPaid(inv: InvoiceRecord): boolean {
  return inv.paidAt != null || inv.status === "paid";
}

export function isCancelled(inv: InvoiceRecord): boolean {
  return inv.cancelledAt != null || inv.status === "cancelled";
}

/** Verworfen (weich) oder gelöscht. */
export function isDiscarded(inv: InvoiceRecord): boolean {
  return inv.discardedAt != null || inv.status === "deleted";
}

/** Zurückgezogen/ungültig (nie wirksam geworden). */
export function isWithdrawn(inv: InvoiceRecord): boolean {
  return inv.withdrawnAt != null || inv.status === "void";
}

/** Rechnung zählt fachlich noch (nicht verworfen, nicht zurückgezogen). */
export function isLive(inv: InvoiceRecord): boolean {
  return !isDiscarded(inv) && !isWithdrawn(inv);
}

export function isApproved(inv: InvoiceRecord): boolean {
  return inv.reviewStatus === "approved";
}

export function needsCorrection(inv: InvoiceRecord): boolean {
  return inv.reviewStatus === "rejected" || inv.reviewStatus === "revision";
}

export function isSent(inv: InvoiceRecord): boolean {
  return inv.sentToBrandAt != null;
}

/** RE-42: echtes Versandsignal braucht Datum UND Zähler > 0. */
export function hasSendSignal(inv: InvoiceRecord): boolean {
  return inv.sentToBrandAt != null && inv.sentToBrandCount > 0;
}

export function inCollection(inv: InvoiceRecord): boolean {
  return inv.dunningStage === DUNNING_COLLECTION;
}

export function hasDunningStage(inv: InvoiceRecord): boolean {
  return inv.dunningStage !== DUNNING_NONE && inv.dunningStage !== DUNNING_COLLECTION;
}

/** Zahlungslauf: genehmigt, versendet, offen, nicht storniert, nicht im Inkasso (RE-34). */
export function inPaymentRun(inv: InvoiceRecord): boolean {
  return isLive(inv) && isApproved(inv) && isSent(inv) && !isPaid(inv) && !isCancelled(inv) && !inCollection(inv);
}

/**
 * Creator-Rechnung an die Agentur, nur anteilig ausgezahlt bzw. verrechnet (GELD-R3-1, 0091): ein Teil ist gebucht, der Rest offen,
 * die Auszahlung nicht als vollständig bestätigt. Solange schließen weder „Zahlung melden“ noch „Zahlung bestätigen“ oder
 * „Auszahlung bestätigen“ die Rechnung; der Rest läuft über den nächsten Auszahlungslauf.
 */
export function partialPayoutOpen(inv: InvoiceRecord): boolean {
  const paid = inv.paidCents ?? 0;
  return (inv.kind ?? "talent_to_agency") === "talent_to_agency" && !isPaid(inv) && !isCancelled(inv) && inv.adminPaymentConfirmedAt == null
    && paid > 0 && paid < inv.amountGrossCents;
}

/** Zahlungsbestätigung der Agentur zählt nur im Zahlungslauf. */
export function confirmationActive(inv: InvoiceRecord): boolean {
  return inv.adminPaymentConfirmedAt != null && inPaymentRun(inv);
}

/** Geltende Fälligkeit: nach Versand die wirksame, davor die des Ausstellers. */
export function currentDueDate(inv: InvoiceRecord): IsoDate | null {
  return inv.effectiveDueDate ?? inv.documentDueDate ?? inv.issuerDueDate;
}

/** Fälligkeit laut Beleg („ursprüngliche“ Fälligkeit, Basis für Tageszähler und Zinsen). */
export function originalDueDate(inv: InvoiceRecord): IsoDate | null {
  return inv.documentDueDate ?? inv.issuerDueDate;
}

/** Offene Nachfrage beim Talent: gestellt und seitdem nicht beantwortet (RE-11). */
export function hasOpenPaymentCheck(inv: InvoiceRecord): boolean {
  if (!inv.overdueCheckRequestedAt) return false;
  if (!inv.talentPaymentResponseAt || !inv.talentPaymentResponse) return true;
  return inv.talentPaymentResponseAt < inv.overdueCheckRequestedAt;
}

/** Talent hat „noch nicht angekommen“ geantwortet (und die Antwort ist die aktuelle). */
export function talentSaysStillOverdue(inv: InvoiceRecord): boolean {
  return inv.talentPaymentResponse === "still_overdue" && !hasOpenPaymentCheck(inv);
}

/** Rechnung, die per Mail an eine Marke geht (Zugang zählt, 0055). */
export function goesToBrand(inv: InvoiceRecord): boolean {
  return (inv.kind === "agency_to_brand" || inv.kind === "talent_to_brand") && !inv.creditFor;
}

/**
 * Zugang beim Empfänger (0055): versendet an eine Marke, aber ohne zugestellte Rechnungsmail bzw. vermerkte Zustellung.
 * Ohne Zugang läuft keine Frist, es gibt keinen Verzug und keine Mahnung. Alte Datensätze ohne Angabe gelten als zugegangen.
 */
export function awaitsDelivery(inv: InvoiceRecord): boolean {
  return isSent(inv) && goesToBrand(inv) && inv.deliveredAt === null;
}

/** Forderung als uneinbringlich ausgebucht (0055) */
export function isBadDebt(inv: InvoiceRecord): boolean {
  return inv.badDebtAt != null;
}

/** Fälligkeit, nach der eine Rechnung überfällig wird: bei Auszahlungen an Creator die Fälligkeit der Auszahlung (0055). */
export function overdueBase(inv: InvoiceRecord): IsoDate | null {
  if (inv.recipientIsOwnOrg && inv.payoutDueDate !== undefined) return inv.payoutDueDate;
  return currentDueDate(inv);
}
